import type { ReactNode } from 'react';
import { useShellTexts } from '../i18n/use-shell-texts';
import { Icon } from './icon';

export type ChipVariant =
  'default' | 'neutral' | 'primary' | 'success' | 'warning' | 'danger' | 'info';
export type ChipSize = 'sm' | 'md' | 'lg';

/** Bootstrap 4 `.tag` is 75% of the surrounding text. */
const SIZES: Record<ChipSize, string> = {
  sm: 'text-[75%]',
  md: 'text-[75%]',
  lg: 'text-[85%]',
};

/** Solid legacy `.tag-*` colours; primary and success share the brand green. */
const VARIANTS: Record<ChipVariant, string> = {
  default: 'bg-neutral',
  neutral: 'bg-neutral',
  primary: 'bg-brand',
  success: 'bg-brand',
  warning: 'bg-warning',
  danger: 'bg-danger',
  info: 'bg-info',
};

/** A `bg-*` in `className` replaces the variant's colour rather than competing with it. */
const OWN_BACKGROUND = /(^|\s)bg-/;

/**
 * A status badge or removable tag: the legacy `.tag` (`tag-pill` with `pill`). `className` takes
 * one-off colours (e.g. `bg-[#5e9583]`) that the variants do not cover.
 */
export function Chip({
  variant = 'default',
  size = 'md',
  pill = false,
  title,
  className = '',
  onRemove,
  children,
}: {
  variant?: ChipVariant;
  size?: ChipSize;
  /** Fully rounded (`tag-pill`). */
  pill?: boolean;
  /** Tooltip. */
  title?: string;
  className?: string;
  /** Shows a remove button when set. */
  onRemove?: () => void;
  children: ReactNode;
}) {
  const t = useShellTexts().kit;
  return (
    <span
      title={title}
      className={`inline-flex items-center gap-[.25em] py-[.25em] text-center leading-none font-bold whitespace-nowrap text-white ${pill ? 'rounded-full px-[.6em]' : 'rounded-bs px-[.4em]'} ${SIZES[size]} ${OWN_BACKGROUND.test(className) ? '' : VARIANTS[variant]} ${className}`}
    >
      {children}
      {onRemove ? (
        <button
          type="button"
          aria-label={t.remove}
          onClick={event => {
            event.stopPropagation();
            onRemove();
          }}
          className="inline-flex items-center justify-center rounded-full hover:bg-black/10"
        >
          <Icon name="close" />
        </button>
      ) : null}
    </span>
  );
}
